import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Image,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  AppCard,
  AppSelect,
  AnimatedSearchInput,
  OutlineButton,
  SortableTh,
  StatusBadge,
  TablePagination,
  TableLoader,
} from '../../../components/ui';
import { useAdminHeaderRefresh } from '../../../contexts/AdminHeaderRefreshContext';
import { VERIFICATION_OPTIONS } from '../../../data/master/people';
import { usePagination } from '../../../hooks/usePagination';
import { useTableSort } from '../../../hooks/useTableSort';
import { listAppUsers, exportAppUsersExcel } from '../../../repositories/appUser.repository';
import { toApiStatus } from '../../../models/_helpers';
import {
  ageFromDob,
  formatDateDisplay,
  formatDobDisplay,
} from '../../../utils/personName';
import { MdFileDownload } from 'react-icons/md';

const USER_TYPE_OPTIONS = [
  { value: '', label: 'All members' },
  { value: 'service_provider', label: 'Active: Seller' },
  { value: 'agency', label: 'Active: Buyer' },
];

const FILTER_H = '40px';

const MEMBER_SEARCH_HINTS = {
  '': [
    'Search by member name…',
    'Search by agency / trade name…',
    'Search by phone…',
    'Search by email…',
    'Search by Aadhaar number…',
    'Search by employee code…',
    'Search by gender (male / female / other)…',
    'Search for village…',
    'Search by GST…',
    'Search by district / taluka…',
  ],
  agency: [
    'Search by agency name…',
    'Search by owner name…',
    'Search by employee code…',
    'Search by phone…',
    'Search by email…',
    'Search by Aadhaar number…',
    'Search by GST…',
    'Search for village…',
    'Search by district / taluka…',
  ],
  service_provider: [
    'Search by member name…',
    'Search by phone…',
    'Search by email…',
    'Search by Aadhaar number…',
    'Search by gender (male / female / other)…',
    'Search for village…',
    'Search by education…',
    'Search by district / taluka…',
  ],
};

const OFFERING_LABELS = {
  equipment: 'Equipment',
  material_supply: 'Material',
  special_services: 'Special Services',
};

function statusBadgeProps(status) {
  if (status === 'Verified') return { status: 'verified' };
  if (status === 'Rejected') return { status: 'error' };
  return { status: 'pending' };
}

function roleUiStatus(user, key) {
  const raw = user?.roles?.[key]?.verificationStatus;
  if (!raw) return null;
  return String(raw).charAt(0).toUpperCase() + String(raw).slice(1);
}

function locationLine(user) {
  const parts = [user.village, user.district, user.state].filter((p) => String(p || '').trim());
  return parts.length ? parts.join(' · ') : '';
}

function buyerTradeName(user) {
  return (
    user?.roles?.agency?.tradeName ||
    user.tradeName ||
    ''
  ).trim();
}

function buyerGst(user) {
  return (user?.roles?.agency?.gstNumber || user.gstNumber || '').trim();
}

function sellerOfferingsLabel(user) {
  const fromRole = user?.roles?.service_provider?.offerings;
  const list = Array.isArray(fromRole) && fromRole.length
    ? fromRole
    : Array.isArray(user.offerings)
      ? user.offerings
      : [];
  if (!list.length) return '';
  return list.map((id) => OFFERING_LABELS[id] || id).join(' · ');
}

function genderLabel(value) {
  const g = String(value || '').trim();
  if (!g) return '';
  return g.charAt(0).toUpperCase() + g.slice(1).toLowerCase();
}

function MemberAvatar({ name, photoUrl }) {
  const initials = String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  if (photoUrl) {
    return (
      <Image
        src={photoUrl}
        alt=""
        boxSize="36px"
        borderRadius="full"
        objectFit="cover"
        flexShrink={0}
        fallback={
          <Flex
            boxSize="36px"
            borderRadius="full"
            bg="bg.input"
            borderWidth="1px"
            borderColor="border.subtle"
            align="center"
            justify="center"
            flexShrink={0}
          >
            <Text fontSize="11px" fontWeight="700" color="text.secondary">
              {initials || '?'}
            </Text>
          </Flex>
        }
      />
    );
  }

  return (
    <Flex
      boxSize="36px"
      borderRadius="full"
      bg="bg.input"
      borderWidth="1px"
      borderColor="border.subtle"
      align="center"
      justify="center"
      flexShrink={0}
    >
      <Text fontSize="11px" fontWeight="700" color="text.secondary">
        {initials || '?'}
      </Text>
    </Flex>
  );
}

/** Status + optional Active mark on one line; details stacked below. */
function RoleCell({ statusLabel, isActiveRole, detailLines = [] }) {
  if (!statusLabel) {
    return <Text color="text.secondary">—</Text>;
  }
  const lines = detailLines.filter((l) => l && String(l).trim());
  return (
    <Box minW="120px">
      <Flex align="center" gap={1.5} wrap="wrap">
        <StatusBadge size="sm" {...statusBadgeProps(statusLabel)}>
          {statusLabel}
        </StatusBadge>
        {isActiveRole ? (
          <Text
            as="span"
            fontSize="10px"
            fontWeight="700"
            color="accent.amber"
            letterSpacing="0.02em"
          >
            Active
          </Text>
        ) : null}
      </Flex>
      {lines.map((line) => (
        <Text
          key={line}
          mt={1}
          fontSize="12px"
          color="text.secondary"
          whiteSpace="normal"
          wordBreak="break-word"
          title={line}
        >
          {line}
        </Text>
      ))}
    </Box>
  );
}

export default function AppUsersPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAppUsers({
        pageSize: 200,
        userType: typeFilter || undefined,
        verificationStatus: statusFilter ? toApiStatus(statusFilter) : undefined,
        search: search.trim() || undefined,
      });
      setUsers(res.items || []);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load members',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }, [toast, typeFilter, statusFilter, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useAdminHeaderRefresh(load);

  const { sorted, sort, toggleSort } = useTableSort(users, {
    fullName: (u) => u.ownerName || u.fullName,
    tradeName: (u) => buyerTradeName(u),
    userTypeLabel: (u) => u.userTypeLabel,
    phone: (u) => u.phone,
    email: (u) => u.email,
    gender: (u) => u.gender,
    dob: (u) => u.dob,
    createdAt: (u) => u.createdAt,
    village: (u) => u.village,
    verificationStatus: (u) => u.verificationStatus,
    registrationStatusLabel: (u) => u.registrationStatusLabel,
  });

  const {
    pageItems,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
  } = usePagination(sorted);

  const statusOptions = useMemo(
    () => [{ value: '', label: 'All statuses' }, ...VERIFICATION_OPTIONS],
    [],
  );

  const searchHints = MEMBER_SEARCH_HINTS[typeFilter] || MEMBER_SEARCH_HINTS[''];

  const colSpan = 7;

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportAppUsersExcel({
        userType: typeFilter || undefined,
        verificationStatus: statusFilter ? toApiStatus(statusFilter) : undefined,
        search: search.trim() || undefined,
      });
      toast({
        title: 'Export started',
        description: 'All members matching current filters were included.',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Export failed',
        status: 'warning',
        position: 'top-right',
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <AppCard overflow="hidden">
        <Flex
          direction={{ base: 'column', xl: 'row' }}
          gap={3}
          mb={4}
          align={{ xl: 'end' }}
        >
          <Flex
            direction={{ base: 'column', sm: 'row' }}
            gap={3}
            flex="1"
            minW={0}
            w="100%"
            align={{ sm: 'end' }}
            flexWrap="wrap"
          >
            <Box flex="1" minW={{ base: '100%', sm: '220px' }}>
              <AnimatedSearchInput
                hints={searchHints}
                h={FILTER_H}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
            <Box
              w={{ base: '100%', sm: '158px' }}
              maxW={{ sm: '158px' }}
              flexShrink={0}
            >
              <AppSelect
                label="Active role"
                labelFontSize="13px"
                h={FILTER_H}
                value={typeFilter}
                onChange={setTypeFilter}
                options={USER_TYPE_OPTIONS}
              />
            </Box>
            <Box
              w={{ base: '100%', sm: '132px' }}
              maxW={{ sm: '132px' }}
              flexShrink={0}
            >
              <AppSelect
                label="Verification"
                labelFontSize="13px"
                h={FILTER_H}
                value={statusFilter}
                onChange={setStatusFilter}
                options={statusOptions}
              />
            </Box>
          </Flex>
          <Flex
            gap={2}
            align="end"
            flexShrink={0}
            w={{ base: '100%', xl: 'auto' }}
            flexWrap="wrap"
          >
            <OutlineButton
              size="sm"
              h={FILTER_H}
              minH={FILTER_H}
              flex={{ base: '1', xl: 'none' }}
              minW={{ base: 0, xl: '120px' }}
              px={4}
              onClick={() => {
                setSearch('');
                setTypeFilter('');
                setStatusFilter('');
              }}
            >
              Clear filters
            </OutlineButton>
            <OutlineButton
              size="sm"
              h={FILTER_H}
              minH={FILTER_H}
              flex={{ base: '1', xl: 'none' }}
              minW={{ base: 0, xl: '132px' }}
              px={4}
              leftIcon={<MdFileDownload />}
              onClick={handleExport}
              isLoading={exporting}
              isDisabled={loading || exporting}
            >
              Export Excel
            </OutlineButton>
          </Flex>
        </Flex>

        {loading ? (
          <TableLoader />
        ) : (
          <>
            <Box overflowX="auto">
              <Table size="sm" minW="1100px">
                <Thead>
                  <Tr>
                    <SortableTh
                      columnKey="fullName"
                      sort={sort}
                      onSort={toggleSort}
                      minW="260px"
                      w="26%"
                    >
                      Name
                    </SortableTh>
                    <SortableTh columnKey="phone" sort={sort} onSort={toggleSort} minW="140px">
                      Contact
                    </SortableTh>
                    <SortableTh columnKey="gender" sort={sort} onSort={toggleSort} minW="90px">
                      Gender
                    </SortableTh>
                    <SortableTh columnKey="dob" sort={sort} onSort={toggleSort} minW="110px">
                      Birthdate
                    </SortableTh>
                    <SortableTh columnKey="createdAt" sort={sort} onSort={toggleSort} minW="110px">
                      Registered
                    </SortableTh>
                    <SortableTh columnKey="tradeName" sort={sort} onSort={toggleSort} minW="160px">
                      Buyer
                    </SortableTh>
                    <SortableTh
                      columnKey="verificationStatus"
                      sort={sort}
                      onSort={toggleSort}
                      minW="160px"
                    >
                      Seller
                    </SortableTh>
                  </Tr>
                </Thead>
                <Tbody>
                  {pageItems.map((user) => {
                    const loc = locationLine(user);
                    const trade = buyerTradeName(user);
                    const gst = buyerGst(user);
                    const offerings = sellerOfferingsLabel(user);
                    const buyerStatus = roleUiStatus(user, 'agency');
                    const sellerStatus = roleUiStatus(user, 'service_provider');
                    const gender = genderLabel(user.gender);
                    const displayName =
                      user.ownerName || user.fullName || user.phone || '—';
                    const ageYears = user.dob ? ageFromDob(user.dob) : null;

                    return (
                      <Tr
                        key={user.id}
                        cursor="pointer"
                        _hover={{ bg: 'bg.hover' }}
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                      >
                        <Td minW="260px" maxW="360px">
                          <Flex align="flex-start" gap={3} w="100%">
                            <MemberAvatar
                              name={displayName}
                              photoUrl={user.profilePhotoUrl}
                            />
                            <Box flex="1" minW={0}>
                              <Text fontWeight="600" title={displayName}>
                                {displayName}
                              </Text>
                              {loc ? (
                                <Text
                                  fontSize="12px"
                                  color="text.secondary"
                                  noOfLines={2}
                                  title={loc}
                                >
                                  {loc}
                                </Text>
                              ) : null}
                              <Text fontSize="11px" color="text.secondary" mt={0.5}>
                                {user.registrationStatusLabel || '—'}
                              </Text>
                            </Box>
                          </Flex>
                        </Td>
                        <Td>
                          <Text fontFamily="mono" fontSize="13px">
                            {user.phone || '—'}
                          </Text>
                          {user.email ? (
                            <Text fontSize="11px" color="text.secondary" noOfLines={1} title={user.email}>
                              {user.email}
                            </Text>
                          ) : (
                            <Text fontSize="11px" color="text.secondary">
                              No email
                            </Text>
                          )}
                        </Td>
                        <Td>
                          <Text fontSize="13px">{gender || '—'}</Text>
                        </Td>
                        <Td>
                          {user.dob ? (
                            <Box>
                              <Text fontSize="13px">{formatDobDisplay(user.dob)}</Text>
                              {ageYears != null ? (
                                <Text fontSize="12px" color="text.secondary" mt={0.5}>
                                  {ageYears} yrs
                                </Text>
                              ) : null}
                            </Box>
                          ) : (
                            <Text fontSize="13px">—</Text>
                          )}
                        </Td>
                        <Td>
                          <Text fontSize="13px">
                            {user.createdAt ? formatDateDisplay(user.createdAt) : '—'}
                          </Text>
                        </Td>
                        <Td>
                          <RoleCell
                            statusLabel={buyerStatus}
                            isActiveRole={user.userType === 'agency'}
                            detailLines={[
                              trade || null,
                              gst ? `GST ${gst}` : null,
                            ]}
                          />
                        </Td>
                        <Td>
                          <RoleCell
                            statusLabel={sellerStatus}
                            isActiveRole={user.userType === 'service_provider'}
                            detailLines={[offerings || null]}
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                  {!pageItems.length ? (
                    <Tr>
                      <Td colSpan={colSpan}>
                        <Text py={8} textAlign="center" color="text.secondary">
                          No members found.
                        </Text>
                      </Td>
                    </Tr>
                  ) : null}
                </Tbody>
              </Table>
            </Box>
            <TablePagination
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </AppCard>
    </Box>
  );
}
